(() => {
  'use strict';
  if (window.pbxInfiniteScrollReady) return;
  window.pbxInfiniteScrollReady = true;

  const initialized = new WeakMap();
  const active = new Set();
  let scrollFrame = 0;
  const cacheKey = 'postbus.catalog-scroll.v1';
  const maxRestorePages = 8;
  const backForward = () => performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward';
  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : fallback;
  };
  const cards = grid => Array.from(grid.children).filter(child => child.matches('.pbx-card'));
  const format = (template, values) => String(template).replace(/__(COUNT|TOTAL|FROM|TO)__/g, (_, key) => String(values[key] ?? ''));
  const cardKey = card => {
    const handle = card.querySelector('[data-pb26-save]')?.dataset.handle;
    if (handle) return handle;
    const href = card.querySelector('.pbx-card__link[href]')?.getAttribute('href');
    if (!href) return '';
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin && url.pathname.includes('/products/') ? url.pathname.split('/products/')[1] : '';
    } catch (_) { return ''; }
  };

  function checkScrollPosition() {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      active.forEach(state => state.checkPosition?.());
    });
  }

  function readCache() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(cacheKey) || '[]');
      return Array.isArray(saved) ? saved.filter(item => item && typeof item.url === 'string' && Date.now() - item.time < 1800000).slice(-6) : [];
    } catch (_) { return []; }
  }

  function savePosition(state) {
    if (!state.catalog.isConnected || state.route.length > 1600 || state.currentPage - state.firstPage > maxRestorePages) return;
    try {
      const saved = readCache().filter(item => item.url !== state.route);
      saved.push({ url: state.route, page: state.currentPage, firstPage: state.firstPage, total: state.total, y: Math.max(0, window.scrollY), time: Date.now() });
      sessionStorage.setItem(cacheKey, JSON.stringify(saved.slice(-6)));
    } catch (_) {}
  }

  function initCatalog(catalog) {
    if (initialized.has(catalog)) return;
    const grid = catalog.querySelector('.pbx-catalog__grid');
    const controls = catalog.querySelector('[data-pbx-load-more]');
    if (!grid || !controls || !window.fetch || !window.DOMParser || !window.AbortController) return;

    const link = controls.querySelector('[data-pbx-load-more-link]');
    const status = controls.querySelector('[data-pbx-load-status]');
    const progress = controls.querySelector('[data-pbx-load-count]');
    const initialCards = cards(grid);
    const initialKeys = initialCards.map(cardKey);
    if (!initialCards.length || initialKeys.some(key => !key) || new Set(initialKeys).size !== initialKeys.length) return;

    const initialURL = new URL(location.href);
    initialURL.hash = '';
    initialURL.searchParams.delete('section_id');
    initialURL.searchParams.delete('sections');
    const firstPage = Math.max(1, number(controls.dataset.currentPage, number(initialURL.searchParams.get('page'), 1)));
    const sectionId = catalog.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
    const validSectionId = sectionId && /^[\w-]{1,180}$/.test(sectionId) ? sectionId : '';
    const state = {
      catalog, grid, controls, link, status, progress,
      route: initialURL.pathname + initialURL.search,
      firstPage, currentPage: firstPage,
      pageSize: Math.max(1, number(controls.dataset.pageSize, initialCards.length)),
      total: number(controls.dataset.total, initialCards.length),
      keys: new Set(initialKeys),
      next: link?.getAttribute('href') || '',
      idleLabel: link?.textContent.trim() || 'Visa fler',
      loading: false, blocked: false, restoring: false, disposed: false,
      observer: null, controller: null
    };

    function nextURL(href, expectedPage) {
      const url = new URL(href, initialURL);
      if (url.origin !== initialURL.origin || url.pathname !== initialURL.pathname || number(url.searchParams.get('page')) !== expectedPage) throw new Error('Unexpected collection page');
      for (const key of new Set(initialURL.searchParams.keys())) {
        if (key === 'page' || key === 'section_id' || key === 'sections' || url.searchParams.has(key)) continue;
        initialURL.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
      }
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.hash = '';
      return url;
    }

    if (state.next) {
      try { state.next = nextURL(state.next, firstPage + 1).href; }
      catch (_) { return; }
    }

    initialized.set(catalog, state);
    active.add(state);
    controls.dataset.enhanced = 'true';
    if (status) {
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
    }

    function updateProgress() {
      const count = cards(grid).length;
      const offset = (state.firstPage - 1) * state.pageSize;
      if (progress) progress.textContent = format(controls.dataset.countLabel || 'Visar __COUNT__ av __TOTAL__ produkter', {
        COUNT: count, TOTAL: state.total, FROM: offset + 1, TO: Math.min(offset + count, state.total)
      });
      controls.dataset.currentPage = String(state.currentPage);
      controls.dataset.loadedCount = String(count);
    }

    function observe() {
      if (state.loading || state.restoring || state.blocked || state.disposed || !state.next) return;
      if (state.observer) {
        state.observer.disconnect();
        state.observer.observe(controls);
      }
      checkScrollPosition();
    }

    function updateControls() {
      controls.dataset.state = state.loading ? 'loading' : state.blocked ? 'error' : state.next ? 'ready' : 'complete';
      grid.setAttribute('aria-busy', String(state.loading));
      if (!link) return;
      link.hidden = !state.next;
      link.textContent = state.loading ? (controls.dataset.loadingLabel || 'Laddar fler produkter…') : state.idleLabel;
      if (state.next) link.href = state.next;
      if (state.loading) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    }

    async function load(manual = false) {
      if (state.loading || !state.next || state.disposed || !catalog.isConnected || (state.blocked && !manual)) return false;
      state.loading = true;
      state.blocked = false;
      state.observer?.disconnect();
      updateControls();
      if (status && !state.restoring) status.textContent = controls.dataset.loadingLabel || 'Laddar fler produkter…';

      const controller = new AbortController();
      state.controller = controller;
      const timer = window.setTimeout(() => controller.abort(), 15000);
      try {
        const expectedPage = state.currentPage + 1;
        const url = nextURL(state.next, expectedPage);
        if (validSectionId) url.searchParams.set('section_id', validSectionId);
        const response = await fetch(url.href, {
          credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Collection request failed');
        const finalURL = new URL(response.url || url.href);
        if (finalURL.origin !== initialURL.origin || finalURL.pathname !== initialURL.pathname) throw new Error('Unexpected collection response');
        const html = await response.text();
        if (!html || html.length > 2500000) throw new Error('Invalid collection response');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incomingCatalog = doc.querySelector('[data-pbx-catalog]');
        const incomingGrid = incomingCatalog?.querySelector('.pbx-catalog__grid');
        const incomingControls = incomingCatalog?.querySelector('[data-pbx-load-more]');
        if (!incomingCatalog || !incomingGrid || incomingCatalog.getAttribute('aria-labelledby') !== catalog.getAttribute('aria-labelledby')) throw new Error('Mismatched collection');
        if (validSectionId && incomingCatalog.closest('.shopify-section')?.id !== `shopify-section-${validSectionId}`) throw new Error('Mismatched collection section');
        const incomingPage = number(incomingControls?.dataset.currentPage, number(incomingCatalog.querySelector('.pbx-catalog__pagination .current')?.textContent, expectedPage));
        if (incomingPage !== expectedPage) throw new Error('Mismatched collection page');
        const incomingCards = cards(incomingGrid);
        const incomingKeys = incomingCards.map(cardKey);
        if (!incomingCards.length || incomingKeys.some(key => !key || state.keys.has(key)) || new Set(incomingKeys).size !== incomingKeys.length) throw new Error('Empty or repeated collection page');
        const followingLink = incomingControls?.querySelector('[data-pbx-load-more-link]') || incomingCatalog.querySelector('.pbx-catalog__pagination .next a');
        const followingHref = followingLink?.getAttribute('href');
        const following = followingHref ? nextURL(followingHref, expectedPage + 1).href : '';
        if (state.disposed || !catalog.isConnected) return false;

        const fragment = document.createDocumentFragment();
        Array.from(incomingGrid.children).forEach(child => fragment.append(child));
        grid.append(fragment);
        incomingKeys.forEach(key => state.keys.add(key));
        state.currentPage = expectedPage;
        state.total = number(incomingControls?.dataset.total, state.total);
        state.next = following;
        updateProgress();
        document.dispatchEvent(new CustomEvent('postbus:products-loaded', {
          detail: { catalog, grid, count: incomingCards.length, total: cards(grid).length }
        }));
        if (status && !state.restoring) status.textContent = format(controls.dataset.loadedLabel || '__COUNT__ nya produkter har laddats.', {
          COUNT: incomingCards.length, TOTAL: cards(grid).length
        });
        if (!following && status && !state.restoring && state.firstPage === 1 && cards(grid).length >= state.total) {
          status.textContent = format(controls.dataset.completeLabel || 'Du har nått slutet av listan.', { COUNT: cards(grid).length, TOTAL: state.total });
        }
        if (manual) incomingCards[0].querySelector('.pbx-card__link[href], a[href]')?.focus({ preventScroll: true });
        return true;
      } catch (_) {
        if (!state.disposed && catalog.isConnected) {
          state.blocked = true;
          if (status) status.textContent = controls.dataset.errorLabel || 'Det gick inte att ladda fler just nu. Tryck på Visa fler för att försöka igen.';
        }
        return false;
      } finally {
        window.clearTimeout(timer);
        state.controller = null;
        state.loading = false;
        if (!state.disposed) {
          updateControls();
          observe();
        }
      }
    }

    link?.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !state.next) return;
      event.preventDefault();
      if (!state.restoring) void load(true);
    });

    if ('IntersectionObserver' in window && link) {
      state.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !state.blocked && !state.restoring) void load();
      }, { rootMargin: '0px 0px 640px 0px', threshold: 0 });
    }

    state.checkPosition = () => {
      if (state.loading || state.restoring || state.blocked || state.disposed || !state.next || !link || !catalog.isConnected) return;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (grid.getBoundingClientRect().top <= viewportHeight && controls.getBoundingClientRect().top <= viewportHeight + 640) void load();
    };

    state.destroy = () => {
      state.disposed = true;
      state.observer?.disconnect();
      state.controller?.abort();
      active.delete(state);
    };
    updateProgress();
    updateControls();

    async function restore() {
      const saved = backForward() ? readCache().find(item => item.url === state.route && item.firstPage === state.firstPage && item.total === state.total) : null;
      const target = number(saved?.page, state.firstPage);
      if (!saved || target <= state.firstPage || target - state.firstPage > maxRestorePages || !Number.isFinite(saved.y)) {
        observe();
        return;
      }
      state.restoring = true;
      while (state.currentPage < target && state.next && !state.disposed) {
        if (!await load()) break;
      }
      state.restoring = false;
      if (state.currentPage === target && !state.disposed && catalog.isConnected) {
        if (status) status.textContent = '';
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: Math.max(0, saved.y), behavior: 'instant' })));
      }
      observe();
    }
    void restore();
  }

  function init(root = document) {
    if (root.matches?.('[data-pbx-catalog]')) initCatalog(root);
    root.querySelectorAll?.('[data-pbx-catalog]').forEach(initCatalog);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    active.forEach(state => { if (event.target.contains(state.catalog)) state.destroy(); });
  });
  window.addEventListener('pagehide', () => active.forEach(savePosition));
  window.addEventListener('scroll', checkScrollPosition, { passive: true });
  window.addEventListener('resize', checkScrollPosition, { passive: true });
  window.addEventListener('pageshow', event => {
    if (event.persisted) active.forEach(state => {
      if (state.next && !state.blocked && !state.loading) state.observer?.observe(state.controls);
    });
    checkScrollPosition();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
